import { Platform, Pressable, View, useColorScheme } from 'react-native';
import { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { ScreenProps, Tabs } from 'expo-router';
import { Text } from '@/components/ui/text';
import { Icon } from '~/global/components/icon/Icon';
import { colors } from '@/lib/colors';
import { CurrentUserAvatar } from '~/user/components/current-user-avatar/CurrentUserAvatar';
import { cn } from '@/lib/utils';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export type TabScreenBaseOptions = typeof Tabs.Screen extends (
  props: ScreenProps<infer O>,
) => null
  ? O
  : never;

export type TabsScreenOptions = TabScreenBaseOptions & {
  hidden?: boolean;
  canonical?: string[];
};

export interface TabBarProps extends BottomTabBarProps {}

export const TabBar = ({ state, descriptors, navigation }: TabBarProps) => {
  const colorScheme = useColorScheme();
  const isDark = colorScheme === 'dark';
  const insets = useSafeAreaInsets();

  const handleAICoachPress = () => {
    navigation.navigate('chat');
  };

  return (
    <View
      className="absolute bottom-0 left-0 right-0 flex-row justify-center items-end gap-3 px-5 pt-3"
      style={
        Platform.OS === 'android'
          ? { paddingBottom: insets.bottom + 8 }
          : { paddingBottom: 32 }
      }
    >
      {/* Glass-morphic tab bar container */}
      <View
        className={cn(
          'flex-row justify-between items-stretch flex-1 p-1 gap-2 rounded-[32px]',
          isDark
            ? 'bg-tab-bar-glass/80 border border-white/[0.15]'
            : 'bg-white/80 border border-black/[0.15]',
        )}
        style={{
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.06,
          shadowRadius: 8,
          elevation: 4,
        }}
      >
        {state.routes
          .map((route, index) => {
            const { options } = descriptors[route.key] as {
              options: TabsScreenOptions;
            };
            const label =
              options.tabBarLabel !== undefined
                ? options.tabBarLabel
                : options.title !== undefined
                  ? options.title
                  : route.name;

            // Hide tabs with hidden option or chat tab (AI coach button handles it)
            if (options.hidden || route.name === 'chat') return null;

            const isFocused =
              state.index === index ||
              options.canonical?.includes(state.routeNames[state.index]);

            const onPress = () => {
              const event = navigation.emit({
                type: 'tabPress',
                target: route.key,
                canPreventDefault: true,
              });

              if (!isFocused && !event.defaultPrevented) {
                navigation.navigate(route.name, route.params);
              }
            };

            const onLongPress = () => {
              navigation.emit({
                type: 'tabLongPress',
                target: route.key,
              });
            };

            const isProfileTab = route.name === 'profile';

            // Active/inactive colors (from Pencil design: #EDBE00 active, #00000090/#00000070 inactive)
            const activeIconColor = isDark ? colors.dark.accent : '#EDBE00';
            const inactiveIconColor = isDark
              ? 'rgba(255, 255, 255, 0.56)'
              : 'rgba(0, 0, 0, 0.56)';
            const activeTextColor = activeIconColor;
            const inactiveTextColor = isDark
              ? 'rgba(255, 255, 255, 0.44)'
              : 'rgba(0, 0, 0, 0.44)';

            return (
              <Pressable
                key={route.key}
                testID={`nav-${route.name}`}
                onPress={onPress}
                onLongPress={onLongPress}
                className="flex-col items-center gap-1 px-1 flex-1 py-1.5"
                style={{
                  borderRadius: isFocused ? 30 : 20,
                  backgroundColor: isFocused
                    ? isDark
                      ? 'rgba(255,255,255,0.08)'
                      : 'rgba(0,0,0,0.08)'
                    : 'transparent',
                }}
              >
                {isProfileTab ? (
                  <View
                    className={cn(
                      'w-6 h-6 rounded-full overflow-hidden border-2',
                      isDark ? 'border-white/60' : 'border-black/60',
                    )}
                  >
                    <CurrentUserAvatar className="w-6 h-6" />
                  </View>
                ) : (
                  options?.tabBarIcon?.({
                    focused: isFocused,
                    color: isFocused ? activeIconColor : inactiveIconColor,
                  } as Record<string, unknown>)
                )}
                <Text
                  numberOfLines={1}
                  className={cn(
                    'text-xs',
                    isFocused ? 'font-semibold' : 'font-medium',
                  )}
                  style={{
                    color: isFocused ? activeTextColor : inactiveTextColor,
                  }}
                >
                  {typeof label === 'string'
                    ? label
                    : label({ focused: isFocused } as Record<string, never>)}
                </Text>
              </Pressable>
            );
          })
          .filter(Boolean)}
      </View>

      {/* AI Coach FAB (always visible) */}
      <View className="items-center">
        <Pressable
          onPress={handleAICoachPress}
          className={cn(
            'flex items-center justify-center w-16 h-16 rounded-full',
            isDark ? 'bg-ai-coach-yellow' : 'bg-ai-coach-yellow-light',
          )}
          style={{
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.06,
            shadowRadius: 8,
            elevation: 4,
          }}
        >
          <Icon name="Bot" size={24} color="#000000" />
        </Pressable>
      </View>
    </View>
  );
};
